import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  ListItemIcon,
  Backdrop,
  Divider,
  Button,
  ListItemText,
  Tooltip
} from '@mui/material';
import { useNotification } from '../layouts/Layout';
import { MoreVert, Send, Delete, MailOutline as GmailIcon, Visibility, Close, DragIndicator } from '@mui/icons-material';
import EditIcon from '../icons/EditIcon';
import InquiryEmailDialog from './InquiryEmailDialog';
import AddInquiryForm from './AddInquiryForm';
import projectService, { Inquiry, Package } from '../../services/projectService';
import api from '../../services/api'; // Import instancji api

interface InquiriesTabProps {
  projectName: string;
  projectData?: any;
  onRefreshData?: () => void; // Opcjonalna funkcja do odświeżania danych
  initialPackageForInquiry?: {
    packageId: string;
    packageName: string;
    projectId: string;
    projectName: string;
  } | null;
  onPackageInquiryProcessed?: () => void; // Funkcja wywoływana po przetworzeniu zapytania z pakietu
}

const InquiriesTab: React.FC<InquiriesTabProps> = ({ 
  projectName, 
  projectData, 
  onRefreshData, 
  initialPackageForInquiry, 
  onPackageInquiryProcessed 
}) => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();
  // Konfiguracja kolumn jak w PackagesTab
  const headerCellSx: any = React.useMemo(() => ({
    backgroundColor: (t: any) => (t.palette.mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.06)'),
    position: 'relative'
  }), []);
  const [colResizeMode, setColResizeMode] = useState<boolean>(false);
  const [dragCol, setDragCol] = useState<{ key: string; startX: number; startW: number; rightKey?: string; startWRight?: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    id: 160,
    creator: 80,
    pkg: 220,
    contractor: 240,
    subject: 260,
    status: 140,
    actions: 80
  }), []);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(defaultColumnWidths);
  const getWidthNum = React.useCallback((key: string) => columnWidths[key] ?? defaultColumnWidths[key] ?? 40, [columnWidths, defaultColumnWidths]);
  const getColW = React.useCallback((key: string) => `${getWidthNum(key)}px`, [getWidthNum]);
  const ALL_COLUMNS = React.useMemo<string[]>(() => ['id','creator','pkg','contractor','subject','status','actions'], []);
  const STORAGE_PREFIX = 'inquiriesTableCol';
  const VISIBLE_KEY = `${STORAGE_PREFIX}:visible`;
  const ORDER_KEY = `${STORAGE_PREFIX}:order`;
  const WIDTHS_KEY = `${STORAGE_PREFIX}:widths`;
  const STORAGE_KEY = React.useMemo(() => 'projectInquiriesColWidths', []);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const ord = JSON.parse(raw);
        if (Array.isArray(ord) && ord.length) return ord.filter((k:string)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!ord.includes(k)));
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {}
    return {};
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);
  // Panel Dostosuj kolumny
  const [colsOpen, setColsOpen] = useState<boolean>(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [colsPosition, setColsPosition] = useState<{ top:number; left:number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = useState<{ startX:number; startY:number; top:number; left:number } | null>(null);
  const COLUMNS_DIALOG = { width: 305, height: 420 } as const;
  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, colsDragging.top + dy));
      const nextLeft = Math.max(0, Math.min((window?.innerWidth || 0), colsDragging.left + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [colsDragging]);
  // Persist
  React.useEffect(() => { try { localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder)); } catch {} }, [ORDER_KEY, columnOrder]);
  React.useEffect(() => { try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible)); } catch {} }, [VISIBLE_KEY, columnsVisible]);
  React.useEffect(() => { try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(columnWidths)); } catch {} }, [WIDTHS_KEY, columnWidths]);
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY]);
  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [STORAGE_KEY, columnWidths]);
  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, (dragCol.startWRight as number) - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => { window.removeEventListener('mousemove', onMove); };
  }, [dragCol]);
  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {keyId === 'id' ? <Box sx={{ pl: '16px' }}>{node}</Box> : node}
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            const visOrder = (draftOrder ?? columnOrder).filter((k)=> colV(k));
            const idx = visOrder.indexOf(keyId);
            const rightKey = idx >= 0 && idx < visOrder.length - 1 ? visOrder[idx+1] : undefined;
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: getWidthNum(keyId),
              rightKey,
              startWRight: rightKey ? getWidthNum(rightKey) : undefined
            });
          }}
          sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }}
        />
      ) : null}
    </TableCell>
  );
  
  // Interfejs dla lokalnego formatu zapytania (kompatybilny z UI)
  interface InquiryUI {
    id: string;
    packageId: string;
    packageName: string;
    contractorEmail: string;
    subject: string;
    status: string; // Używamy string zamiast union type, aby obsłużyć wszystkie możliwe statusy
    createdAt: string;
    gmailDraftId?: string; // ID draftu w Gmail, jeśli istnieje
    gmailMessageId?: string; // ID wysłanej wiadomości w Gmail, jeśli istnieje
    inviteToCollectifileBoard?: boolean;
  }
  
  // Stan dla lokalnego formatu zapytań
  const [inquiriesUI, setInquiriesUI] = useState<InquiryUI[]>([]);
  
  // Mapowanie statusów z API na UI
  const statusToUI = (status: string, gmailDraftId?: string, gmailMessageId?: string): 'Przygotowane' | 'draft gmail' | 'Wysłane' => {
    // Priorytetowo traktujemy status z bazy danych
    switch (status.toLowerCase()) {
      case 'przygotowane': return 'Przygotowane';
      case 'draft gmail': return 'draft gmail';
      case 'wysłane': return 'Wysłane';
      default: return 'Przygotowane'; // Domyślnie zwracamy 'Przygotowane'
    }
  };
  
  // Mapowanie statusów z UI na API
  const statusToAPI = (status: string): 'Przygotowane' | 'draft gmail' | 'Wysłane' => {
    switch (status) {
      case 'Przygotowane': return 'Przygotowane';
      case 'draft gmail': return 'draft gmail';
      case 'Wysłane': return 'Wysłane';
      default: return 'Przygotowane'; // Domyślnie zwracamy 'Przygotowane'
    }
  };
  
  // Mapowanie statusów UI na API
  const mapNewStatusToOld = (newStatus: string): 'Przygotowane' | 'draft gmail' | 'Wysłane' => {
    switch (newStatus) {
      case 'Przygotowane': return 'Przygotowane';
      case 'draft gmail': return 'draft gmail';
      case 'Wysłane': return 'Wysłane';
      default: return 'Przygotowane'; // Domyślnie zwracamy 'Przygotowane'
    }
  };
  
  // Konwersja z formatu API do formatu UI
  const convertToUIFormat = (inquiry: Inquiry): InquiryUI => {
    console.log('Konwersja zapytania do formatu UI:', JSON.stringify(inquiry, null, 2));
    return {
      id: inquiry.inquiry_id,
      packageId: inquiry.package_id,
      packageName: inquiry.package_name,
      contractorEmail: inquiry.contractor_email,
      subject: inquiry.subject,
      status: statusToUI(inquiry.status, inquiry.gmail_draft_id, inquiry.gmail_message_id),
      createdAt: inquiry.created_at || new Date().toISOString(),
      gmailDraftId: inquiry.gmail_draft_id,
      gmailMessageId: inquiry.gmail_message_id,
      inviteToCollectifileBoard: !!inquiry.invite_to_collectifile_board
    };
  };
  
  // Konwersja z formatu UI do formatu API
  const convertToAPIFormat = (inquiryUI: InquiryUI, projectId: string): Partial<Inquiry> => {
    return {
      // Usuwamy inquiry_id, aby backend mógł wygenerować własne ID
      project_id: projectId,
      package_id: inquiryUI.packageId,
      project_name: projectName, // Dodajemy nazwę projektu
      package_name: inquiryUI.packageName, // Dodajemy nazwę pakietu
      contractor_email: inquiryUI.contractorEmail,
      subject: inquiryUI.subject,
      status: statusToAPI(inquiryUI.status),
      invite_to_collectifile_board: inquiryUI.inviteToCollectifileBoard
    };
  };
  
  // Pobieranie pakietów i zapytań dla projektu
  useEffect(() => {
    if (projectData) {
      setLoading(true);
      setError(null); // Resetuj błąd przy każdym nowym ładowaniu
      
      console.log('=== Rozpoczęcie pobierania danych dla projektu ===');
      // Dodajmy więcej logowania, aby zrozumieć strukturę projectData
      console.log('Dane projektu:', JSON.stringify(projectData, null, 2));
      console.log('Typ project_id:', typeof projectData.project_id);
      console.log('Wartość project_id:', projectData.project_id);
      console.log('Wszystkie klucze w projectData:', Object.keys(projectData));

      // Używamy project_id w formacie "[project_id] [project_name]" zgodnie z wyjaśnieniem użytkownika
      let rawProjectId = projectData.project_id || projectData.id;
      
      console.log(`Surowy identyfikator projektu (oczekiwany format [id] [nazwa] lub samo id): "${rawProjectId}"`);

      if (!rawProjectId) {
        const errorMsg = 'Brak identyfikatora projektu';
        console.error(errorMsg);
        setError(errorMsg);
        showNotification(errorMsg, 'error');
        setLoading(false);
        return;
      }

      // Dodaj obsługę błędów dla przypadku, gdy rawProjectId nie jest stringiem
      if (typeof rawProjectId !== 'string') {
        console.log('Konwersja rawProjectId na string:', String(rawProjectId));
        rawProjectId = String(rawProjectId);
      }

      // Wyodrębnij rzeczywiste ID projektu
      // Zakładamy, że ID może być albo czystym ID, albo w formacie "ID Nazwa"
      const projectIdParts = rawProjectId.split(' ');
      const actualProjectId = projectIdParts[0];
      
      console.log(`Pobieranie danych dla projektu o rzeczywistym ID: ${actualProjectId}`);
      console.log('Stan autoryzacji:', localStorage.getItem('auth_token') ? 'Zalogowany' : 'Niezalogowany');
      
      // Pobierz pakiety dla projektu
      console.log('Wywołanie projectService.getPackagesByProjectId z ID:', actualProjectId);
      projectService.getPackagesByProjectId(actualProjectId)
        .then(packagesData => {
          console.log(`Pobrano ${packagesData.length} pakietów dla projektu ${actualProjectId}`);
          console.log('Przykładowy pakiet:', packagesData.length > 0 ? JSON.stringify(packagesData[0], null, 2) : 'Brak pakietów');
          setPackages(packagesData);
          
          // Pobierz zapytania dla projektu
          console.log('Wywołanie projectService.getInquiriesByProjectId z ID:', actualProjectId);
          return projectService.getInquiriesByProjectId(actualProjectId);
        })
        .then(inquiriesData => {
          console.log(`Pobrano zapytania dla projektu ${actualProjectId}`);
          console.log('Typ inquiriesData:', typeof inquiriesData);
          console.log('Czy inquiriesData jest tablicą:', Array.isArray(inquiriesData));
          console.log('Wszystkie zapytania:', JSON.stringify(inquiriesData, null, 2));
          
          // Sprawdź, czy inquiriesData jest tablicą
          if (!Array.isArray(inquiriesData)) {
            console.error('inquiriesData nie jest tablicą:', typeof inquiriesData);
            console.log('Struktura inquiriesData:', inquiriesData);
            
            // Jeśli nie jest tablicą, ale ma właściwość data, która jest tablicą, użyj jej
            if (inquiriesData && typeof inquiriesData === 'object' && 'data' in inquiriesData && Array.isArray((inquiriesData as any).data)) {
              console.log('Używam inquiriesData.data zamiast inquiriesData');
              inquiriesData = (inquiriesData as any).data;
            } else {
              // W przeciwnym razie utwórz pustą tablicę
              console.log('Tworzę pustą tablicę dla inquiriesData');
              inquiriesData = [];
            }
          }
          
          console.log(`Ostatecznie pobrano ${inquiriesData.length} zapytań dla projektu ${actualProjectId}`);
          setInquiries(inquiriesData);
          
          // Konwertuj zapytania do formatu UI
          console.log('Rozpoczęcie konwersji zapytań do formatu UI');
          const convertedInquiries = inquiriesData.map(inquiry => {
            console.log('Konwersja zapytania:', JSON.stringify(inquiry, null, 2));
            const converted = convertToUIFormat(inquiry);
            console.log('Wynik konwersji:', JSON.stringify(converted, null, 2));
            return converted;
          });
          console.log('Skonwertowane zapytania:', JSON.stringify(convertedInquiries, null, 2));
          setInquiriesUI(convertedInquiries);
          
          // Jeśli nie ma zapytań, pokaż informację
          if (inquiriesData.length === 0) {
            console.log('Brak zapytań dla projektu');
            // Nie ustawiamy błędu, ponieważ brak zapytań to nie błąd
          } else {
            console.log('Przykładowe zapytanie:', JSON.stringify(inquiriesData[0], null, 2));
          }
          
          console.log('=== Zakończenie pobierania danych dla projektu - sukces ===');
        })
        .catch(err => {
          console.error('=== Zakończenie pobierania danych dla projektu - błąd ===');
          console.error('Błąd podczas pobierania danych:', err);
          
          // Szczegółowa obsługa błędów
          if (err.response) {
            // Serwer zwrócił odpowiedź z kodem statusu innym niż 2xx
            console.error('Błąd odpowiedzi:', err.response.data);
            console.error('Status błędu:', err.response.status);
            console.error('Nagłówki odpowiedzi:', err.response.headers);
            
            if (err.response.status === 401) {
              setError('Błąd autoryzacji. Zaloguj się ponownie.');
              showNotification('Błąd autoryzacji. Zaloguj się ponownie.', 'error');
            } else if (err.response.status === 404) {
              setError('Nie znaleziono projektu lub zapytań.');
              showNotification('Nie znaleziono projektu lub zapytań.', 'error');
            } else if (err.response.status === 500) {
              setError('Błąd serwera. Spróbuj ponownie później.');
              showNotification('Błąd serwera. Spróbuj ponownie później.', 'error');
            } else {
              setError(`Błąd: ${err.response.data.message || 'Nieznany błąd'}`);
              showNotification(`Błąd: ${err.response.data.message || 'Nieznany błąd'}`, 'error');
            }
          } else if (err.request) {
            // Żądanie zostało wysłane, ale nie otrzymano odpowiedzi
            console.error('Brak odpowiedzi:', err.request);
            setError('Brak odpowiedzi z serwera. Sprawdź połączenie internetowe.');
            showNotification('Brak odpowiedzi z serwera. Sprawdź połączenie internetowe.', 'error');
          } else {
            // Coś poszło nie tak podczas konfiguracji żądania
            console.error('Błąd:', err.message);
            setError(`Błąd: ${err.message}`);
            showNotification(`Błąd: ${err.message}`, 'error');
          }
          
          
          // Mimo błędu, spróbujmy wyświetlić puste dane
          setInquiries([]);
          setInquiriesUI([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [projectData]);

  // Usunięto dodatkowy efekt do odświeżania danych
  const [openInquiryForm, setOpenInquiryForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<any>(null);
  const [initialPackage, setInitialPackage] = useState<Package | null>(null);
  
  // Efekt do obsługi automatycznego otwierania formularza z predefiniowanym pakietem
  useEffect(() => {
    if (initialPackageForInquiry && packages.length > 0) {
      // Znajdź pakiet w liście pakietów
      const foundPackage = packages.find(pkg => pkg.package_id === initialPackageForInquiry.packageId);
      if (foundPackage) {
        setInitialPackage(foundPackage);
        setEditMode(false);
        setSelectedInquiry(null);
        setOpenInquiryForm(true);
        
        // Zresetuj initialPackageForInquiry po przetworzeniu
        if (onPackageInquiryProcessed) {
          onPackageInquiryProcessed();
        }
      }
    }
  }, [initialPackageForInquiry, packages, onPackageInquiryProcessed]);
  const [inquiryAnchorEl, setInquiryAnchorEl] = useState<null | HTMLElement>(null);
  const [actionInquiryIndex, setActionInquiryIndex] = useState<number>(-1);

  // Funkcje dla zapytań
  const handleOpenInquiryForm = () => {
    setEditMode(false);
    setSelectedInquiry(null);
    setOpenInquiryForm(true);
  };

  const handleOpenEditInquiryForm = () => {
    if (actionInquiryIndex >= 0) {
      const inquiryToEdit = inquiriesUI[actionInquiryIndex];
      if (inquiryToEdit && (inquiryToEdit.status === 'Przygotowane' || inquiryToEdit.status === 'draft gmail')) {
        setSelectedInquiry(inquiryToEdit);
        setEditMode(true);
        setOpenInquiryForm(true);
      }
    }
  };

  const handleCloseInquiryForm = () => {
    setOpenInquiryForm(false);
    setEditMode(false);
    setSelectedInquiry(null);
  };

  const handleAddInquiry = (newInquiryUI: InquiryUI) => {    
    // Jeśli mamy dane projektu, dodaj do API
    if (projectData) {
      // Używamy project_id w formacie "[project_id] [project_name]" zgodnie z wyjaśnieniem użytkownika
      let rawProjectId = projectData.project_id || projectData.id;
      console.log(`Surowy identyfikator projektu przy dodawaniu zapytania (oczekiwany format [id] [nazwa] lub samo id): "${rawProjectId}"`);

      if (!rawProjectId) {
        const errorMsg = 'Nie można dodać zapytania - brak ID projektu';
        console.error(errorMsg);
        showNotification(errorMsg, 'error');
        return;
      }

      // Dodaj obsługę błędów dla przypadku, gdy rawProjectId nie jest stringiem
      if (typeof rawProjectId !== 'string') {
        console.log('Konwersja rawProjectId na string przy dodawaniu zapytania:', String(rawProjectId));
        rawProjectId = String(rawProjectId);
      }
      
      const projectIdParts = rawProjectId.split(' ');
      const actualProjectId = projectIdParts[0];
      
      console.log(`Dodawanie zapytania dla projektu o rzeczywistym ID: ${actualProjectId}`);
      
      const apiInquiry = convertToAPIFormat(newInquiryUI, actualProjectId); 
      console.log('Dane wysyłane do API:', apiInquiry);
      
      projectService.createInquiry(apiInquiry) // Wywołanie API
        .then(createdInquiry => {
          // Aktualizuj stan API
          console.log('Zapytanie utworzone pomyślnie:', createdInquiry);
          setInquiries([...inquiries, createdInquiry]);
          
          // Aktualizuj stan UI dodając nowe zapytanie otrzymane z API
          const newInquiryFromApiUI = convertToUIFormat(createdInquiry);
          setInquiriesUI(prevInquiriesUI => [newInquiryFromApiUI, ...prevInquiriesUI]);
          
          // Pokaż komunikat sukcesu
          showNotification('Zapytanie zostało utworzone pomyślnie', 'success');
          
          // Wywołaj funkcję odświeżania danych, jeśli istnieje
          if (onRefreshData) {
            onRefreshData();
          }
        })
        .catch(err => {
          console.error('Błąd podczas tworzenia zapytania:', err);
          console.error('Szczegóły błędu:', err.response?.data || err.message);
          
          // Pokaż bardziej szczegółowy komunikat o błędzie
          const errorMessage = err.response?.data?.message || err.message || 'Nie udało się utworzyć zapytania. Spróbuj ponownie później.';
          showNotification(errorMessage, 'error');
        });
    }
  };

  const handleEditInquiry = (editedInquiryUI: InquiryUI) => {
    // Aktualizuj lokalny stan UI po ID
    const updatedInquiriesUI = inquiriesUI.map((inquiry) => 
      inquiry.id === editedInquiryUI.id ? editedInquiryUI : inquiry
    );
    setInquiriesUI(updatedInquiriesUI);
    
    // Jeśli mamy dane projektu, aktualizuj w API
    if (projectData) {
      // Używamy project_id w formacie "[project_id] [project_name]" zgodnie z wyjaśnieniem użytkownika
      let rawProjectId = projectData.project_id || projectData.id;
      console.log(`Surowy identyfikator projektu przy aktualizacji zapytania (oczekiwany format [id] [nazwa] lub samo id): "${rawProjectId}"`);
      
      if (!rawProjectId) {
        const errorMsg = 'Nie można zaktualizować zapytania - brak ID projektu';
        console.error(errorMsg);
        showNotification(errorMsg, 'error');
        return;
      }

      // Dodaj obsługę błędów dla przypadku, gdy rawProjectId nie jest stringiem
      if (typeof rawProjectId !== 'string') {
        console.log('Konwersja rawProjectId na string przy aktualizacji zapytania:', String(rawProjectId));
        rawProjectId = String(rawProjectId);
      }
      
      const projectIdParts = rawProjectId.split(' ');
      const actualProjectId = projectIdParts[0];
      
      console.log(`Aktualizacja zapytania dla projektu o rzeczywistym ID: ${actualProjectId}`);
      
      const apiInquiry = convertToAPIFormat(editedInquiryUI, actualProjectId);
      projectService.updateInquiry(editedInquiryUI.id, apiInquiry) // Wywołanie API
        .then(updatedInquiry => {
          // Aktualizuj stan API
          const updatedInquiries = inquiries.map(inquiry => 
            inquiry.inquiry_id === updatedInquiry.inquiry_id ? updatedInquiry : inquiry
          );
          setInquiries(updatedInquiries);
          
          // Pokaż komunikat sukcesu
          showNotification('Status zapytania został zaktualizowany pomyślnie', 'success');
          
          // Wywołaj funkcję odświeżania danych, jeśli istnieje
          if (onRefreshData) {
            onRefreshData();
          }
        })
        .catch(err => {
          console.error('Błąd podczas aktualizacji statusu zapytania:', err);
          showNotification('Nie udało się zaktualizować statusu zapytania. Spróbuj ponownie później.', 'error');
        });
    }
    
  };

  // Funkcja do tworzenia wersji roboczej w Gmailu
  const handleCreateGmailDraft = async (inquiryId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/inquiries/${inquiryId}/create-gmail-draft`);
      if (response.data.success) {
        const draftData = response.data.data;
        showNotification('Wersja robocza e-maila utworzona pomyślnie w Gmailu.', 'success');
        
        // Aktualizuj lokalny stan zapytania z nowym statusem i ID draftu
        setInquiriesUI(prevInquiries => 
          prevInquiries.map(inquiry => 
            inquiry.id === inquiryId 
              ? { 
                  ...inquiry, 
                  status: 'draft gmail',
                  gmailDraftId: draftData.draftId 
                } 
              : inquiry
          )
        );
        
        // Odśwież listę zapytań, aby zaktualizować status
        if (onRefreshData) {
          onRefreshData();
        }
      } else {
        // Sprawdzenie, czy backend wymaga ponownej autoryzacji
        if (response.data.requireReauth) {
           showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
        } else {
           showNotification(response.data.message || 'Nie udało się utworzyć wersji roboczej.', 'error');
        }
      }
    } catch (err: any) {
      console.error('Błąd podczas tworzenia wersji roboczej Gmail:', err);
      // Sprawdzenie, czy błąd z serwera zawiera flagę requireReauth
      if (err.response?.data?.requireReauth) {
         showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
      } else {
         showNotification(err.response?.data?.message || 'Wystąpił błąd podczas tworzenia wersji roboczej.', 'error');
      }
    } finally {
      setLoading(false);
      handleInquiryActionClose(); // Zamknij menu akcji
    }
  };

  // Funkcja do usuwania zapytania
  const handleDeleteInquiry = async (inquiryId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.delete(`/inquiries/${inquiryId}`);
      if (response.data.success) {
        showNotification('Zapytanie zostało usunięte pomyślnie.', 'success');
        
        // Usuń zapytanie z lokalnego stanu
        setInquiriesUI(prevInquiries => prevInquiries.filter(inquiry => inquiry.id !== inquiryId));
        
        // Odśwież listę zapytań
        if (onRefreshData) {
          onRefreshData();
        }
      } else {
        showNotification(response.data.message || 'Nie udało się usunąć zapytania.', 'error');
      }
    } catch (err: any) {
      console.error('Błąd podczas usuwania zapytania:', err);
      showNotification(err.response?.data?.message || 'Wystąpił błąd podczas usuwania zapytania.', 'error');
    } finally {
      setLoading(false);
      handleInquiryActionClose(); // Zamknij menu akcji
    }
  };

  // Funkcja do wysyłania wiadomości z draftu Gmail
  const handleSendEmail = async (inquiryId: string) => {
    setLoading(true);
    setError(null);

    try {
      // Znajdź zapytanie, aby uzyskać ID draftu
      const inquiry = inquiriesUI.find(inq => inq.id === inquiryId);
      if (!inquiry || !inquiry.gmailDraftId) {
        showNotification('Nie można wysłać wiadomości - brak ID draftu.', 'error');
        setLoading(false);
        return;
      }

      // Aktualizuj status w API przed wysłaniem wiadomości
      await projectService.updateInquiryStatus(inquiryId, 'Wysłane');
      
      // Aktualizuj lokalny stan zapytania z nowym statusem
      setInquiriesUI(prevInquiries => 
        prevInquiries.map(inq => 
          inq.id === inquiryId 
            ? { 
                ...inq, 
                status: 'Wysłane'
              } 
            : inq
        )
      );
      
      // Odśwież listę zapytań z bazy danych
      if (onRefreshData) {
        onRefreshData();
      }
      
      showNotification('Status zapytania został zaktualizowany na "Wysłane".', 'success');

      // Wywołaj API do wysłania draftu
      const response = await api.post(`/inquiries/${inquiryId}/send-gmail-draft`, {
        draftId: inquiry.gmailDraftId
      });

      if (response.data.success) {
        const messageData = response.data.data || {};
        showNotification('Wiadomość została wysłana pomyślnie.', 'success');
        
        // Aktualizuj ID wiadomości w lokalnym stanie (jeśli istnieje)
        if (messageData && messageData.messageId) {
          setInquiriesUI(prevInquiries => 
            prevInquiries.map(inq => 
              inq.id === inquiryId 
                ? { 
                    ...inq, 
                    gmailMessageId: messageData.messageId 
                  } 
                : inq
            )
          );
        }
        
        // Odśwież dane zapytań, aby mieć pewność, że mamy najnowsze dane
        if (onRefreshData) {
          onRefreshData();
        }
        
        // Dodatkowo pobierz najnowsze dane zapytania
        try {
          const updatedInquiry = await projectService.getInquiryById(inquiryId);
          if (updatedInquiry) {
            // Aktualizuj stan zapytań
            setInquiries(prevInquiries => 
              prevInquiries.map(inq => 
                inq.inquiry_id === inquiryId ? updatedInquiry : inq
              )
            );
            
            // Aktualizuj stan UI
            const updatedInquiryUI = convertToUIFormat(updatedInquiry);
            setInquiriesUI(prevInquiriesUI => 
              prevInquiriesUI.map(inq => 
                inq.id === inquiryId ? updatedInquiryUI : inq
              )
            );
          }
        } catch (err) {
          console.error('Błąd podczas pobierania zaktualizowanego zapytania:', err);
        }
      } else {
        // Sprawdzenie, czy backend wymaga ponownej autoryzacji
        if (response.data.requireReauth) {
           showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
        } else {
           showNotification(response.data.message || 'Nie udało się wysłać wiadomości.', 'error');
        }
      }
    } catch (err: any) {
      console.error('Błąd podczas wysyłania wiadomości Gmail:', err);
      
      // Sprawdzenie, czy błąd z serwera zawiera flagę requireReauth
      if (err.response?.data?.requireReauth) {
         showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
      } 
      // Sprawdzenie, czy draft nie został znaleziony
      else if (err.response?.status === 404 || err.response?.data?.notFound) {
        showNotification(err.response?.data?.message || 'Wersja robocza nie została znaleziona. Mogła zostać usunięta, wygasła lub została już wysłana.', 'warning');
        
        // Odśwież listę zapytań, aby sprawdzić aktualny stan
        if (onRefreshData) {
          onRefreshData();
        }
      } 
      else {
         showNotification(err.response?.data?.message || 'Wystąpił błąd podczas wysyłania wiadomości.', 'error');
      }
    } finally {
      setLoading(false);
      handleInquiryActionClose(); // Zamknij menu akcji
    }
  };

  // Funkcja do wysyłania wiadomości bezpośrednio (bez tworzenia draftu)
  const handleSendDirectEmail = async (inquiryId: string) => {
    setLoading(true);
    setError(null);

    try {
      // Aktualizuj status w API przed wysłaniem wiadomości
      await projectService.updateInquiryStatus(inquiryId, 'Wysłane');
      
      // Aktualizuj lokalny stan zapytania z nowym statusem
      setInquiriesUI(prevInquiries => 
        prevInquiries.map(inq => 
          inq.id === inquiryId 
            ? { 
                ...inq, 
                status: 'Wysłane'
              } 
            : inq
        )
      );
      
      // Odśwież listę zapytań z bazy danych
      if (onRefreshData) {
        onRefreshData();
      }
      
      showNotification('Status zapytania został zaktualizowany na "Wysłane".', 'success');

      // Wywołaj API do wysłania wiadomości bezpośrednio
      const response = await api.post(`/inquiries/${inquiryId}/send-direct-email`);

      if (response.data.success) {
        const messageData = response.data.data || {};
        showNotification('Wiadomość została wysłana pomyślnie.', 'success');
        
        // Aktualizuj ID wiadomości w lokalnym stanie (jeśli istnieje)
        if (messageData && messageData.messageId) {
          setInquiriesUI(prevInquiries => 
            prevInquiries.map(inq => 
              inq.id === inquiryId 
                ? { 
                    ...inq, 
                    gmailMessageId: messageData.messageId 
                  } 
                : inq
            )
          );
        }
        
        // Odśwież dane zapytań, aby mieć pewność, że mamy najnowsze dane
        if (onRefreshData) {
          onRefreshData();
        }
        
        // Dodatkowo pobierz najnowsze dane zapytania
        try {
          const updatedInquiry = await projectService.getInquiryById(inquiryId);
          if (updatedInquiry) {
            // Aktualizuj stan zapytań
            setInquiries(prevInquiries => 
              prevInquiries.map(inq => 
                inq.inquiry_id === inquiryId ? updatedInquiry : inq
              )
            );
            
            // Aktualizuj stan UI
            const updatedInquiryUI = convertToUIFormat(updatedInquiry);
            setInquiriesUI(prevInquiriesUI => 
              prevInquiriesUI.map(inq => 
                inq.id === inquiryId ? updatedInquiryUI : inq
              )
            );
          }
        } catch (err) {
          console.error('Błąd podczas pobierania zaktualizowanego zapytania:', err);
        }
      } else {
        // Sprawdzenie, czy backend wymaga ponownej autoryzacji
        if (response.data.requireReauth) {
           showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
        } else {
           showNotification(response.data.message || 'Nie udało się wysłać wiadomości.', 'error');
        }
      }
    } catch (err: any) {
      console.error('Błąd podczas wysyłania wiadomości Gmail:', err);
      // Sprawdzenie, czy błąd z serwera zawiera flagę requireReauth
      if (err.response?.data?.requireReauth) {
         showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
      } else {
         showNotification(err.response?.data?.message || 'Wystąpił błąd podczas wysyłania wiadomości.', 'error');
      }
    } finally {
      setLoading(false);
      handleInquiryActionClose(); // Zamknij menu akcji
    }
  };

  // Funkcja do sprawdzania statusu wiadomości w Gmail
  const checkGmailMessageStatus = async (inquiryId: string, draftId: string) => {
    try {
      const response = await api.get(`/inquiries/${inquiryId}/check-gmail-status?draftId=${draftId}`);
      if (response.data.success) {
        const { isSent, messageId } = response.data.data;
        
        if (isSent) {
          // Aktualizuj lokalny stan zapytania z nowym statusem i ID wiadomości (jeśli istnieje)
          setInquiriesUI(prevInquiries => 
            prevInquiries.map(inquiry => 
              inquiry.id === inquiryId 
                ? { 
                    ...inquiry, 
                    status: 'Wysłane',
                    gmailMessageId: messageId || null 
                  } 
                : inquiry
            )
          );
          
          // Aktualizuj status w API
          await projectService.updateInquiryStatus(inquiryId, 'Wysłane');
          
          // Odśwież listę zapytań
          if (onRefreshData) {
            onRefreshData();
          }
        }
      }
    } catch (err) {
      console.error('Błąd podczas sprawdzania statusu wiadomości Gmail:', err);
    }
  };

  // Efekt do okresowego sprawdzania statusu wiadomości w Gmail
  useEffect(() => {
    // Sprawdź tylko zapytania z draftem Gmail, które nie są jeszcze oznaczone jako wysłane
    const draftInquiries = inquiriesUI.filter(
      inquiry => inquiry.status === 'draft gmail' && inquiry.gmailDraftId && !inquiry.gmailMessageId
    );
    
    if (draftInquiries.length > 0) {
      const checkInterval = setInterval(() => {
        draftInquiries.forEach(inquiry => {
          if (inquiry.gmailDraftId) {
            checkGmailMessageStatus(inquiry.id, inquiry.gmailDraftId);
          }
        });
      }, 60000); // Sprawdzaj co minutę
      
      return () => clearInterval(checkInterval);
    }
  }, [inquiriesUI]);


  const handleInquiryActionClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
    event.stopPropagation();
    setActionInquiryIndex(index);
    setInquiryAnchorEl(event.currentTarget);
  };

  const handleInquiryActionClose = () => {
    setInquiryAnchorEl(null);
  };

  // Dialog podglądu/edycji wiadomości
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const handleOpenEmailDialog = () => {
    setOpenEmailDialog(true);
    handleInquiryActionClose();
  };
  const handleCloseEmailDialog = () => setOpenEmailDialog(false);

  // Deep-link: jeśli URL zawiera ?inquiry=ID – otwórz dialog wiadomości dla tego zapytania
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const inquiryId = sp.get('inquiry');
      if (inquiryId && inquiriesUI && inquiriesUI.length > 0) {
        const idx = inquiriesUI.findIndex(i => String(i.id) === String(inquiryId));
        if (idx >= 0) {
          setActionInquiryIndex(idx);
          setOpenEmailDialog(true);
        }
      }
    } catch {}
    // tylko gdy lista zapytań gotowa
  }, [inquiriesUI]);

  // Aktualizacja statusu zapytania
  const handleUpdateInquiryStatus = (inquiryIndex: number, newStatus: string) => {
    const inquiryUI = inquiriesUI[inquiryIndex];
    if (!inquiryUI) return;
    
    // Mapujemy nowy status na stary format akceptowany przez API
    const apiStatus = mapNewStatusToOld(newStatus);
    
    // Aktualizuj lokalny stan UI
    const updatedInquiriesUI = [...inquiriesUI];
    updatedInquiriesUI[inquiryIndex] = {
      ...updatedInquiriesUI[inquiryIndex],
      status: statusToUI(newStatus)
    };
    setInquiriesUI(updatedInquiriesUI);
    
    // Jeśli mamy dane projektu, aktualizuj status w API
    if (projectData) {
      projectService.updateInquiryStatus(inquiryUI.id, apiStatus)
        .then(updatedInquiry => {
          // Aktualizuj stan API
          const updatedInquiries = inquiries.map(inquiry => 
            inquiry.inquiry_id === updatedInquiry.inquiry_id ? updatedInquiry : inquiry
          );
          setInquiries(updatedInquiries);
          
          // Pokaż komunikat sukcesu
          showNotification('Status zapytania został zaktualizowany pomyślnie', 'success');
          
          // Wywołaj funkcję odświeżania danych, jeśli istnieje
          if (onRefreshData) {
            onRefreshData();
          }
        })
        .catch(err => {
          console.error('Błąd podczas aktualizacji statusu zapytania:', err);
          showNotification('Nie udało się zaktualizować statusu zapytania. Spróbuj ponownie później.', 'error');
        });
    }
  };

  // Wysłanie zapytania (zmiana statusu na "Wysłane")
  const handleSendInquiry = (inquiryIndex: number) => {
    handleUpdateInquiryStatus(inquiryIndex, 'Wysłane');
  };

  const handleInquiryActionSelect = (action: string) => {
    const inquiryUI = inquiriesUI[actionInquiryIndex];
    if (inquiryUI) {
      switch (action) {
        case 'send':
          if (inquiryUI.status === 'Przygotowane') {
            handleSendInquiry(actionInquiryIndex);
          }
          break;
        case 'link':
          if (inquiryUI.status === 'Wysłane') {
            // Symulacja otwarcia linku do wiadomości Gmail
            window.open(`https://mail.google.com/mail/u/0/#sent/subject:${encodeURIComponent(inquiryUI.subject)}`, '_blank');
          }
          break;
      }
    }
    handleInquiryActionClose();
  };

  // Usunięto mechanizm zaznaczania wierszy i checkboxy

  // Funkcja do generowania inicjałów z imienia i nazwiska
  const getInitials = (firstName?: string, lastName?: string): string => {
    if (!firstName && !lastName) return '-';
    
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    
    return `${firstInitial}${lastInitial}`;
  };


  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Pasek akcji – jak w Pakietach: ikona + i „Dostosuj kolumny” */}
      <Box sx={{ position: 'absolute', top: -36, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleOpenInquiryForm} disabled={loading} data-hotkey-add="true">
            <Box component="img" src="/assets/icons/add.svg" alt="Dodaj" sx={{ width: 22, height: 22 }} />
          </IconButton>
        </Box>
        <Box sx={{ width: 16 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              alignSelf: 'flex-end',
              bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={() => { setColResizeMode(true); setColsOpen(true); setColsPosition({ top: 0, left: 0 }); }}
          >
            <Box
              component="img"
              alt="kolumny"
              src={'/assets/icons/frame-344.svg?v=2'}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, color: 'error.dark' }}>
          <Typography>{error}</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            mt: 0,
            height: 'auto',
            overflow: 'auto',
            position: 'relative',
            border: '1px solid #d0d0d6',
            borderBottom: '1px solid #d0d0d6',
            borderRadius: 0,
            borderTopRightRadius: 4,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            overflowX: 'hidden',
            overflowY: 'auto',
            boxShadow: 'none',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { width: 0, height: 0 }
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
              '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
              '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
              '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
            }}
          >
            {(() => {
              const ord = (draftOrder ?? columnOrder);
              return (
                <colgroup>
                  {ord.filter((k)=>k!=='actions').map((key) => (
                    colV(key) ? <col key={`col-${key}`} style={{ width: getColW(key) }} /> : null
                  ))}
                  {colV('actions') ? <col key="col-actions" style={{ width: getColW('actions') }} /> : null}
                </colgroup>
              );
            })()}
            <TableHead sx={{ backgroundColor: (t)=> t.palette.mode==='light' ? undefined : 'rgba(255,255,255,0.06)' }}>
              <TableRow>
                {(draftOrder ?? columnOrder).filter((k)=>k !== 'actions').map((key) => colV(key) ? renderResizableTh(key, (
                  key === 'id' ? 'ID' :
                  key === 'creator' ? 'Utworzył' :
                  key === 'pkg' ? 'Pakiet' :
                  key === 'contractor' ? 'Kontrahent' :
                  key === 'subject' ? 'Temat' :
                  key === 'status' ? 'Status' : ''
                ), key === 'status' ? 'left' : key === 'creator' ? 'center' : undefined) : null)}
                {colV('actions') ? renderResizableTh('actions', '', 'right') : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {inquiriesUI
                .map((inquiry, index) => {
                return (
                  <TableRow
                    hover
                    key={inquiry.id}
                    sx={{
                      '&:hover': { backgroundColor: '#F5F5FF !important' },
                      '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' },
                      '&.Mui-selected': { backgroundColor: '#F5F5FF !important' },
                      '&.Mui-selected:hover': { backgroundColor: '#F5F5FF !important' }
                    }}
                  >
                    {colV('id') ? (
                      <TableCell sx={{ width: getColW('id') }}>
                        <Box sx={{ pl: '16px' }}>{inquiry.id}</Box>
                      </TableCell>
                    ) : null}
                    {colV('creator') ? (
                      <TableCell align="center" sx={{ width: getColW('creator') }}>
                        {getInitials(inquiries.find(inq => inq.inquiry_id === inquiry.id)?.first_name, 
                                    inquiries.find(inq => inq.inquiry_id === inquiry.id)?.last_name)}
                      </TableCell>
                    ) : null}
                    {colV('pkg') ? (<TableCell sx={{ width: getColW('pkg') }}>
                      <Box sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>
                        {inquiry.packageName}
                      </Box>
                    </TableCell>) : null}
                    {colV('contractor') ? (<TableCell sx={{ width: getColW('contractor') }}>
                      <Box sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>
                        {inquiry.contractorEmail}
                      </Box>
                    </TableCell>) : null}
                    {colV('subject') ? (<TableCell sx={{ width: getColW('subject') }}>
                      <Box sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>
                        {inquiry.subject}
                      </Box>
                    </TableCell>) : null}
                    {colV('status') ? (<TableCell sx={{ width: getColW('status') }}>
                      {(() => {
                        const statusStyles: Record<string, { light: { bg: string; color: string }; dark: { bg: string; color: string }; text: string }> = {
                          'Przygotowane': { light: { bg: '#E3F2FD', color: '#1976D2' }, dark: { bg: 'rgba(25,118,210,0.18)', color: '#90CAF9' }, text: 'Przygotowane' },
                          'draft gmail': { light: { bg: '#FFF8E1', color: '#FF8F00' }, dark: { bg: 'rgba(255,193,7,0.18)', color: '#FFD54F' }, text: 'Draft Gmail' },
                          'Wysłane': { light: { bg: '#E8F5E9', color: '#2E7D32' }, dark: { bg: 'rgba(46,125,50,0.18)', color: '#A5D6A7' }, text: 'Wysłane' },
                        };
                        const palette = (statusStyles[inquiry.status] || { light: { bg: '#f5f5f5', color: '#000' }, dark: { bg: 'rgba(255,255,255,0.08)', color: '#fff' }, text: inquiry.status });
                        return (
                          <Box
                            sx={{
                              backgroundColor: (t)=> t.palette.mode==='light' ? palette.light.bg : palette.dark.bg,
                              color: (t)=> t.palette.mode==='light' ? palette.light.color : palette.dark.color,
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              display: 'inline-block',
                              fontSize: '0.75rem',
                              fontWeight: 'medium',
                              textAlign: 'center'
                            }}
                          >
                            {palette.text}
                          </Box>
                        );
                      })()}
                    </TableCell>) : null}
                    {colV('actions') ? (
                      <TableCell align="right" sx={{ width: getColW('actions') }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          <Tooltip title="Podgląd wiadomości" arrow>
                            <span>
                              <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); setActionInquiryIndex(index); handleOpenEmailDialog(); }}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {inquiry.status === 'Przygotowane' && (
                            <Tooltip title="Utwórz draft w Gmail" arrow>
                              <span>
                                <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); handleCreateGmailDraft(inquiry.id); }}>
                                  <GmailIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {inquiry.status === 'Przygotowane' && (
                            <Tooltip title="Wyślij bezpośrednio" arrow>
                              <span>
                                <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); handleSendDirectEmail(inquiry.id); }}>
                                  <Send fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {inquiry.status === 'draft gmail' && (
                            <Tooltip title="Wyślij wiadomość (z draftu)" arrow>
                              <span>
                                <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); handleSendEmail(inquiry.id); }}>
                                  <Send fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <IconButton size="small" onClick={(event) => handleInquiryActionClick(event, index)} aria-label="więcej">
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Okno „Dostosuj kolumny” */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: `${COLUMNS_DIALOG.height}px`,
            width: `${COLUMNS_DIALOG.width}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto'
          }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e)=>{ e.preventDefault(); }}
                  onDrop={(e)=>{ e.preventDefault(); const src = e.dataTransfer.getData('text/plain'); if (!src || src===key) return; setDraftOrder(prev=>{ const cur = [...(prev||ord)]; const from = cur.indexOf(src); const to = cur.indexOf(key); if (from<0||to<0) return cur; cur.splice(to,0,cur.splice(from,1)[0]); return cur; }); }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'id' ? 'ID' :
                         key === 'creator' ? 'Utworzył' :
                         key === 'pkg' ? 'Pakiet' :
                         key === 'contractor' ? 'Kontrahent' :
                         key === 'subject' ? 'Temat' :
                         key === 'status' ? 'Status' :
                         key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ if (draftColumnsVisible) { setColumnsVisible(draftColumnsVisible); try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(draftColumnsVisible)); } catch {} } if (draftOrder) { const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); setColumnOrder(normalized); try { localStorage.setItem(ORDER_KEY, JSON.stringify(normalized)); } catch {} } setColsOpen(false); setColResizeMode(false); }}>
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>

      <Menu
        anchorEl={inquiryAnchorEl}
        open={Boolean(inquiryAnchorEl)}
        onClose={handleInquiryActionClose}
      >
        {/* Opcja edycji */}
        {actionInquiryIndex >= 0 && (
          <MenuItem onClick={() => handleOpenEditInquiryForm()}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            edytuj
          </MenuItem>
        )}
        {/* Opcja usunięcia */}
        {actionInquiryIndex >= 0 && (
          <MenuItem onClick={() => handleDeleteInquiry(inquiriesUI[actionInquiryIndex].id)}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            usuń
          </MenuItem>
        )}
      </Menu>

      {actionInquiryIndex >= 0 && (
        <InquiryEmailDialog
          open={openEmailDialog}
          onClose={handleCloseEmailDialog}
          inquiryId={inquiriesUI[actionInquiryIndex]?.id}
          initialStatus={inquiriesUI[actionInquiryIndex]?.status}
          onUpdated={() => onRefreshData && onRefreshData()}
        />
      )}

      {openInquiryForm && (
        <AddInquiryForm
          open={openInquiryForm}
          onClose={handleCloseInquiryForm}
          onAdd={handleAddInquiry}
          onEdit={handleEditInquiry}
          packages={packages}
          editMode={editMode}
          inquiryData={selectedInquiry}
          initialPackage={initialPackage}
          projectName={projectName}
          hasCollectifileBoard={!!(projectData?.collectifile_board_id || projectData?.miro_link)}
        />
      )}
    </Box>
  );
};

export default InquiriesTab;
